import { authenticatedProApiFetch } from './pro-api.client';
import { refreshAccessTokenForProApi } from './pro-api-auth';
import { handleSessionExpired } from '../auth/session-expired';
import { getAuthCookie } from '../auth/session-cookie';
import type { DataTransferEntity } from './data-transfer.types';

export type { DataTransferEntity, ChangeSetItem, ChangeSet, DataJob } from './data-transfer.types';
export * from './data-transfer.types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

/** Prefer the live cookie token — callers often hold a stale accessToken after a refresh. */
function resolveAccessToken(fallback?: string | null): string {
  return getAuthCookie('access_token') ?? fallback ?? '';
}

async function fetchProApiWithAuthRetry(
  url: string,
  accessToken: string,
  init: RequestInit = {},
): Promise<Response> {
  const doFetch = (token: string) =>
    fetch(url, {
      ...init,
      headers: {
        ...init.headers,
        Authorization: `Bearer ${token}`,
      },
    });

  let token = resolveAccessToken(accessToken);
  let res = await doFetch(token);
  if (res.status !== 401) return res;

  const refreshed = await refreshAccessTokenForProApi().catch(() => null);
  if (!refreshed) {
    handleSessionExpired();
    return res;
  }
  return doFetch(refreshed);
}

export async function startExport(
  token: string,
  body: {
    entity: DataTransferEntity;
    format: 'json' | 'csv' | 'zip';
    scope: { ids?: string[]; all?: boolean; search?: string };
  },
): Promise<{ jobId: string; status: string }> {
  return authenticatedProApiFetch('/data-transfer/export', resolveAccessToken(token), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function getDataJob(token: string, jobId: string) {
  return authenticatedProApiFetch<import('./data-transfer.types').DataJob>(
    `/data-transfer/jobs/${jobId}`,
    resolveAccessToken(token),
  );
}

export function dataJobDownloadUrl(jobId: string): string {
  return `${API_BASE}/data-transfer/jobs/${jobId}/download`;
}

/**
 * Download a completed export job as a Blob (with 401 → refresh → retry).
 * Not routed through authenticatedProApiFetch because that helper always parses JSON.
 */
export async function downloadDataJob(
  token: string,
  jobId: string,
): Promise<{ blob: Blob; filename: string | null }> {
  const res = await fetchProApiWithAuthRetry(dataJobDownloadUrl(jobId), token, {
    method: 'GET',
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      (data as { error?: string }).error || `Download failed (${res.status})`,
    );
  }
  const disposition = res.headers.get('content-disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? null;
  return { blob: await res.blob(), filename };
}

export async function previewImport(
  token: string,
  file: File,
  deleteMissing = false,
): Promise<import('./data-transfer.types').ImportPreviewResult> {
  const form = new FormData();
  form.append('file', file);
  const url = `${API_BASE}/data-transfer/import/preview?deleteMissing=${deleteMissing}`;
  const res = await fetchProApiWithAuthRetry(url, token, {
    method: 'POST',
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(
      (data as { error?: string }).error || 'Import preview failed',
    );
  }
  return data;
}

export async function applyImport(
  token: string,
  body: {
    entity: DataTransferEntity;
    changeIds: string[];
    items: unknown[];
    mediaBase64?: Record<string, string>;
    previewId?: string | null;
    localization?: import('./data-transfer.types').ExportLocalizationSidecar | null;
    enumGroups?: unknown[];
  },
): Promise<{ jobId: string; status: string }> {
  return authenticatedProApiFetch('/data-transfer/import/apply', resolveAccessToken(token), {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function listCheckpoints(token: string, entity: DataTransferEntity) {
  return authenticatedProApiFetch<{
    checkpoints: Array<{
      id: string;
      entity: string;
      version: number;
      itemCount: number;
      label: string | null;
      createdAt: string;
    }>;
  }>(`/data-transfer/checkpoints?entity=${encodeURIComponent(entity)}`, resolveAccessToken(token));
}

export async function previewCheckpointRestore(token: string, checkpointId: string) {
  return authenticatedProApiFetch<import('./data-transfer.types').CheckpointRestorePreview>(
    `/data-transfer/checkpoints/${encodeURIComponent(checkpointId)}/preview`,
    resolveAccessToken(token),
  );
}

export async function restoreCheckpoint(
  token: string,
  checkpointId: string,
  body: { changeIds: string[] },
): Promise<{ jobId: string; status: string }> {
  return authenticatedProApiFetch(
    `/data-transfer/checkpoints/${encodeURIComponent(checkpointId)}/restore`,
    resolveAccessToken(token),
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
}

/** Poll until job is ready/failed or timeout. */
export async function waitForJob(
  token: string,
  jobId: string,
  opts?: { intervalMs?: number; timeoutMs?: number; onUpdate?: (job: import('./data-transfer.types').DataJob) => void },
) {
  const intervalMs = opts?.intervalMs ?? 1500;
  const timeoutMs = opts?.timeoutMs ?? 10 * 60 * 1000;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    // Re-resolve each poll so a mid-wait refresh is picked up.
    const job = await getDataJob(resolveAccessToken(token), jobId);
    opts?.onUpdate?.(job);
    if (job.status === 'ready' || job.status === 'failed') return job;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  throw new Error('Timed out waiting for job');
}
